import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InvoiceFinancingStatus } from '@hero/schema';
import { ArrowRightIcon, HandCoinsIcon } from '@hero/web-icons';

export interface ActiveFinancingApplicationBannerProps {
  status: InvoiceFinancingStatus | string;
  /** `FinancingApplication._id` — forwarded to `onViewApplication`. */
  applicationId?: string;
  /** Typically navigates to the financing history tab. */
  onViewApplication?: (applicationId: string) => void;
  className?: string;
}

const STATUS_THEME: Record<
  string,
  {
    gradient: string;
    border: string;
    orbA: string;
    orbB: string;
    eyebrow: string;
    title: string;
    description: string;
    badge: string;
  }
> = {
  pending: {
    gradient:
      'linear-gradient(135deg, #FFFBEB, #FEF3C7, #FDE68A, #FEF3C7, #FFFBEB)',
    border: 'border-amber-200/70',
    orbA: 'bg-amber-300/25',
    orbB: 'bg-amber-400/15',
    eyebrow: 'border-amber-300/60 text-amber-900',
    title: 'text-amber-950',
    description: 'text-amber-900/80',
    badge: 'bg-white/80 text-amber-900 ring-1 ring-amber-300/60',
  },
  financed: {
    gradient:
      'linear-gradient(135deg, #ECFDF5, #D1FAE5, #A7F3D0, #D1FAE5, #ECFDF5)',
    border: 'border-emerald-200/70',
    orbA: 'bg-emerald-300/20',
    orbB: 'bg-emerald-400/15',
    eyebrow: 'border-emerald-300/60 text-emerald-900',
    title: 'text-emerald-950',
    description: 'text-emerald-900/80',
    badge: 'bg-white/80 text-emerald-900 ring-1 ring-emerald-300/60',
  },
  rejected: {
    gradient:
      'linear-gradient(135deg, #FEF2F2, #FECACA, #FCA5A5, #FECACA, #FEF2F2)',
    border: 'border-red-200/70',
    orbA: 'bg-red-300/20',
    orbB: 'bg-red-400/10',
    eyebrow: 'border-red-300/60 text-red-900',
    title: 'text-red-950',
    description: 'text-red-900/80',
    badge: 'bg-white/80 text-red-900 ring-1 ring-red-300/60',
  },
  cancelled: {
    gradient:
      'linear-gradient(135deg, #F9FAFB, #F3F4F6, #E5E7EB, #F3F4F6, #F9FAFB)',
    border: 'border-gray-200/80',
    orbA: 'bg-gray-300/20',
    orbB: 'bg-gray-400/10',
    eyebrow: 'border-gray-300/60 text-gray-700',
    title: 'text-gray-900',
    description: 'text-gray-600',
    badge: 'bg-white/80 text-gray-700 ring-1 ring-gray-300/60',
  },
};

const DEFAULT_THEME = STATUS_THEME.pending;

/**
 * In-drawer banner shown once `invoice.financing` exists. Replaces
 * the compact emerald `FinanceInvoiceCTA` with a status-toned card
 * and a single clear status pill (not the stacked "Financing /
 * Under review" chip). Clicking through lands on the financing
 * applications tab with the matching row expanded.
 */
export const ActiveFinancingApplicationBanner: React.FC<
  ActiveFinancingApplicationBannerProps
> = ({ status, applicationId, onViewApplication, className }) => {
  const { t } = useTranslation(['m-financing']);
  const theme = STATUS_THEME[status] ?? DEFAULT_THEME;
  const canNavigate = Boolean(applicationId && onViewApplication);

  const statusLabel = t(
    `m-financing:invoice_drawer.active_application_status.${status}`,
    { defaultValue: status },
  );

  const titleKey = `m-financing:invoice_drawer.active_application_title.${status}` as const;
  const descriptionKey =
    `m-financing:invoice_drawer.active_application_description.${status}` as const;

  const title = t(titleKey, {
    defaultValue:
      status === 'financed'
        ? 'This invoice was financed'
        : status === 'rejected'
          ? 'Financing declined'
          : status === 'cancelled'
            ? 'Financing cancelled'
            : 'Financing in progress',
  });

  const description = t(descriptionKey, {
    defaultValue:
      status === 'financed'
        ? 'View the application for funding and repayment details.'
        : status === 'rejected'
          ? 'Lenders declined this application. View details in your applications.'
          : status === 'cancelled'
            ? 'This application was cancelled before funding.'
            : 'A financing application has been started for this invoice.',
  });

  const handleView = () => {
    if (applicationId && onViewApplication) {
      onViewApplication(applicationId);
    }
  };

  return (
    <div
      role={canNavigate ? 'button' : undefined}
      tabIndex={canNavigate ? 0 : undefined}
      onClick={canNavigate ? handleView : undefined}
      onKeyDown={
        canNavigate
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleView();
              }
            }
          : undefined
      }
      className={`relative overflow-hidden rounded-xl border p-4 ${
        theme.border
      } ${canNavigate ? 'cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40' : ''} ${
        className ?? ''
      }`}
      style={{
        background: theme.gradient,
        backgroundSize: '200% 200%',
      }}>
      <div
        className={`pointer-events-none absolute -top-10 -end-10 size-32 rounded-full ${theme.orbA}`}
      />
      <div
        className={`pointer-events-none absolute -bottom-8 -start-8 size-24 rounded-full ${theme.orbB}`}
      />

      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div
            className={`inline-flex items-center gap-1.5 rounded-full border bg-white/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide backdrop-blur-sm ${theme.eyebrow}`}>
            <HandCoinsIcon className="size-3" />
            {t('m-financing:invoice_drawer.active_application_eyebrow', {
              defaultValue: 'Financing application',
            })}
          </div>
          <div className={`mt-1.5 text-sm font-semibold ${theme.title}`}>{title}</div>
          <div className={`text-xs ${theme.description}`}>{description}</div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${theme.badge}`}>
            {statusLabel}
          </span>
          {canNavigate && (
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold ${theme.title}`}>
              {t('m-financing:invoice_drawer.active_application_view_cta', {
                defaultValue: 'View application',
              })}
              <ArrowRightIcon className="size-3.5 rtl:rotate-180" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActiveFinancingApplicationBanner;
